import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Toast } from '@/components/common/Toast';
import { OTPInput } from '@/components/auth/OTPInput';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { SEO } from '@/components/seo/SEO';
import { CANONICAL_URLS } from '@/utils/canonicalUrl';

interface FormData {
  phone: string;
  email: string;
  password: string;
  name: string;
  referralCode?: string;
}

interface Agreement {
  allAgree: boolean;
  terms: boolean;
  privacy: boolean;
  age: boolean;
  privacyDelegate: boolean;
  location?: boolean;
  marketing?: boolean;
}

export function BuyerSignupPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    phone: '',
    email: '',
    password: '',
    name: '',
    referralCode: ''
  });
  
  const [agreement, setAgreement] = useState<Agreement>({
    allAgree: false,
    terms: false,
    privacy: false,
    age: false,
    privacyDelegate: false,
    location: false,
    marketing: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
  
  // OTP 인증 상태
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [, setVerificationToken] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();
  const [nextAllowedAt, setNextAllowedAt] = useState<string | undefined>();

  const handleBack = () => {
    navigate(-1);
  };

  const handleTermsView = (type: 'terms' | 'privacy' | 'delegate' | 'location') => {
    // 각 약관 페이지로 새 탭에서 리디렉트
    const titleMap = {
      terms: '서비스이용약관(일반 이용자용)',
      privacy: '개인정보수집이용동의서',
      delegate: '개인정보처리방침',
      location: '위치기반서비스이용약관'
    };
    
    const encodedTitle = encodeURIComponent(titleMap[type]);
    window.open(`/mypage/terms-policies/title/${encodedTitle}`, '_blank');
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === 'phone') {
      value = value.replace(/[^0-9]/g, '');
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAllAgree = () => {
    const newValue = !agreement.allAgree;
    setAgreement({
      allAgree: newValue,
      terms: newValue,
      privacy: newValue,
      age: newValue,
      privacyDelegate: newValue,
      location: newValue,
      marketing: newValue
    });
  };

  const handleAgreementChange = (field: keyof Agreement) => {
    setAgreement(prev => {
      const newAgreement = { ...prev, [field]: !prev[field] };
      
      const requiredChecked = 
        newAgreement.terms && 
        newAgreement.privacy && 
        newAgreement.age && 
        newAgreement.privacyDelegate;
      
      const optionalChecked = 
        newAgreement.location && 
        newAgreement.marketing;
      
      newAgreement.allAgree = requiredChecked && (optionalChecked || false);
      
      return newAgreement;
    });
  };

  const isFormValid = () => {
    const requiredFieldsFilled = formData.phone && formData.email && formData.password && formData.name;
    const passwordValid = formData.password && formData.password.length >= 8;
    
    const requiredAgreed = 
      agreement.terms && 
      agreement.privacy && 
      agreement.age && 
      agreement.privacyDelegate;
    
    return requiredFieldsFilled && passwordValid && requiredAgreed && isEmailVerified;
  };

  // OTP 발송
  const handleSendOTP = async () => {
    if (!formData.email) {
      setToast({ show: true, message: '이메일을 입력해주세요' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setToast({ show: true, message: '올바른 이메일 형식을 입력해주세요' });
      return;
    }

    setIsResending(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('send-otp', {
        body: { 
          email: formData.email,
          purpose: 'signup',
          name: formData.name
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setShowOTPInput(true);
        setRemainingAttempts(data.remainingAttempts);
        setToast({ show: true, message: data.message });
      } else {
        setOtpError(data?.error || '인증번호 발송에 실패했습니다.');
        setRemainingAttempts(data?.remainingAttempts);
        setNextAllowedAt(data?.nextAllowedAt);
      }
    } catch (error: any) {
      console.error('OTP 발송 오류:', error);
      setOtpError(error.message || '인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  // OTP 검증
  const handleVerifyOTP = async (otp: string) => {
    setIsOTPLoading(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('verify-otp', {
        body: { 
          email: formData.email,
          otp,
          purpose: 'signup'
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setIsEmailVerified(true);
        setVerificationToken(data.verificationToken);
        setShowOTPInput(false);
        setToast({ show: true, message: '이메일 인증이 완료되었습니다!' });
      } else {
        setOtpError(data?.error || '인증번호가 올바르지 않습니다.');
        setRemainingAttempts(data?.remainingAttempts);
      }
    } catch (error: any) {
      console.error('OTP 검증 오류:', error);
      setOtpError(error.message || '인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsOTPLoading(false);
    }
  };

  const handleSignup = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!isFormValid()) {
      setToast({ show: true, message: '필수 항목을 모두 입력하고 약관에 동의해주세요' });
      return;
    }

    setIsLoading(true);
    try {
      const { success, error, user } = await signUp(formData.email, formData.password, formData.name, 'buyer', formData.phone);

      if (success) {
        const referralCode = formData.referralCode?.trim();
        if (user && referralCode) {
          await supabase.rpc('link_referral_code', {
            p_user_id: user.id,
            p_referral_code: referralCode
          });
        }

        setToast({ show: true, message: '회원가입이 완료되었습니다! 에코딜을 이용해 보세요.' });
        setTimeout(() => {
          navigate('/signup/success');
        }, 2000);
      } else {
        setToast({ show: true, message: error || '회원가입에 실패했습니다.' });
      }
    } catch (error) {
      setToast({ show: true, message: '회원가입 중 오류가 발생했습니다' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="구매자 회원가입 - 에코딜"
        description="에코딜 구매자 회원가입을 하고 마감할인 상품을 저렴하게 구매하세요."
        canonical={CANONICAL_URLS.BUYER_SIGNUP}
      />
      <PageContainer>
        {/* Header */}
        <div className="flex items-center justify-center relative mb-8">
          <button
            onClick={handleBack}
            className="absolute left-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              className="w-6 h-6"
            />
          </button>
          <h1 className="text-2xl font-bold text-[#222222]">회원가입</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSignup} className="space-y-4">
          {/* Phone */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              전화번호(필수): '-' 없이 번호만 입력
            </label>
            <div className="relative">
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="예시) 01012345678"
                className="w-full h-[32px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
              />
              <span className="absolute left-0 top-0 text-[#51C854]">*</span>
            </div>
            <p className="text-sm text-[#606060] mt-1">
              주문정보를 알려드리는 데 사용되니 정확히 입력해 주세요
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              이메일(필수)
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="아이디로 사용하실 이메일을 입력해주세요"
                  className={`w-full h-[32px] px-3 text-base bg-[#F5F5F5] border-[1.5px] rounded-lg focus:outline-none placeholder:text-[#606060] ${
                    isEmailVerified ? 'border-[#4CD964] bg-green-50' : 'border-[#51C854]'
                  }`}
                  disabled={isEmailVerified}
                />
                <span className="absolute left-0 top-0 text-[#51C854]">*</span>
                {isEmailVerified && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <span className="text-[#4CD964] text-sm">✓ 인증완료</span>
                  </div>
                )}
              </div>
              
              {!isEmailVerified && (
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={isResending}
                  className={`px-4 h-[32px] text-sm font-medium rounded-lg transition-all ${
                    !isResending
                      ? 'bg-[#4CD964] text-white hover:bg-[#45B85C] active:scale-95'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isResending ? '인증중...' : '인증하기'}
                </button>
              )}
            </div>
            
            {/* OTP 입력 */}
            {showOTPInput && !isEmailVerified && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                <div className="text-sm text-gray-600 mb-3 text-center">
                  <strong>{formData.email}</strong>로 발송된 인증번호를 입력해주세요
                </div>
                
                <OTPInput
                  onComplete={handleVerifyOTP}
                  onResend={handleSendOTP}
                  isLoading={isOTPLoading}
                  isResending={isResending}
                  error={otpError}
                  remainingAttempts={remainingAttempts}
                  nextAllowedAt={nextAllowedAt}
                />
              </div>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              비밀번호(필수)
            </label>
            <div className="relative">
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="비밀번호를 입력해주세요"
                className="w-full h-[32px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
              />
              <span className="absolute left-0 top-0 text-[#51C854]">*</span>
            </div>
            <p className="text-sm text-[#606060] mt-1">8자리 이상 입력해주세요</p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              이름(필수)
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="본인의 이름을 입력해주세요"
                className="w-full h-[32px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
              />
              <span className="absolute left-0 top-0 text-[#51C854]">*</span>
            </div>
          </div>

          {/* Referral Code */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              추천인코드(선택)
            </label>
            <input
              type="text"
              value={formData.referralCode}
              onChange={(e) => handleInputChange('referralCode', e.target.value)}
              placeholder="추천인코드를 입력해주세요"
              className="w-full h-[32px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#606060] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* Agreement */}
          <div className="mt-8 space-y-3">
            {/* All Agree */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={agreement.allAgree}
                onChange={handleAllAgree}
                className="w-4 h-4 border-[1.5px] border-[#222222] rounded"
              />
              <span className="text-base font-medium text-[#222222]">전체 동의</span>
            </label>

            <div className="border-t border-gray-200 pt-3 space-y-3">
              {/* Required */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreement.age}
                  onChange={() => handleAgreementChange('age')}
                  className="w-4 h-4 border-[1.5px] border-[#222222] rounded"
                />
                <span className="text-base text-[#222222]">만 14세 이상입니다 (필수)</span>
              </label>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreement.terms}
                    onChange={() => handleAgreementChange('terms')}
                    className="w-4 h-4 border-[1.5px] border-[#222222] rounded"
                  />
                  <span className="text-base text-[#222222]">서비스 이용약관 동의 (필수)</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleTermsView('terms')}
                  className="text-sm text-blue-600 underline hover:text-blue-800 transition-colors cursor-pointer ml-1"
                >
                  [더보기]
                </button>
              </div>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreement.privacy}
                    onChange={() => handleAgreementChange('privacy')}
                    className="w-4 h-4 border-[1.5px] border-[#222222] rounded"
                  />
                  <span className="text-base text-[#222222]">개인정보 수집•이용 동의 (필수)</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleTermsView('privacy')}
                  className="text-sm text-blue-600 underline hover:text-blue-800 transition-colors cursor-pointer ml-1"
                >
                  [더보기]
                </button>
              </div>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreement.privacyDelegate}
                    onChange={() => handleAgreementChange('privacyDelegate')}
                    className="w-4 h-4 border-[1.5px] border-[#222222] rounded"
                  />
                  <span className="text-base text-[#222222]">개인정보 처리방침 동의 (필수)</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleTermsView('delegate')}
                  className="text-sm text-blue-600 underline hover:text-blue-800 transition-colors cursor-pointer ml-1"
                >
                  [더보기]
                </button>
              </div>

              {/* Optional */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreement.location}
                      onChange={() => handleAgreementChange('location')}
                      className="w-4 h-4 border-[1.5px] border-[#222222] rounded"
                    />
                    <span className="text-base text-[#222222]">위치기반서비스 이용약관 동의 (선택)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => handleTermsView('location')}
                    className="text-sm text-blue-600 underline hover:text-blue-800 transition-colors cursor-pointer ml-1"
                  >
                    [더보기]
                  </button>
                </div>
                <p className="text-sm text-[#606060] ml-6">동의하지 않을 시 기능이 제한될 수 있습니다</p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreement.marketing}
                  onChange={() => handleAgreementChange('marketing')}
                  className="w-4 h-4 border-[1.5px] border-[#222222] rounded"
                />
                <span className="text-base text-[#222222]">광고/이벤트 수신 동의 (선택)</span>
              </label>
            </div>
          </div>

          {/* Signup Button */}
          <button
            type="submit"
            disabled={isLoading || !isFormValid()}
            className={`w-full h-[31px] mt-8 font-semibold text-xl rounded-xl transition-colors disabled:opacity-50 ${
              isFormValid() && !isLoading
                ? 'bg-[#51C854] text-white hover:bg-[#46B649]'
                : 'bg-[#DEDEDE] text-[#999999] cursor-not-allowed'
            }`}
          >
            {isLoading ? '처리 중...' : '가입하기'}
          </button>
        </form>
      </PageContainer>

      {/* Toast Message */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </div>
  );
}
